import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Users, Check, Square, CheckSquare } from "lucide-react";

export default function RosterSelectorModal({ show, setShow, allEmployees, currentRequiredIds, onSave }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // Sync internal selected array when modal opens with currently active tracked IDs
  useEffect(() => {
    if (show) {
      setSelectedIds(currentRequiredIds || []);
    }
  }, [show, currentRequiredIds]);

  if (!show) return null;

  const handleToggleEmployee = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredEmployees.length) {
      // Clear only the currently filtered items if all are selected
      const filteredItemIds = filteredEmployees.map(e => e.id);
      setSelectedIds(prev => prev.filter(id => !filteredItemIds.includes(id)));
    } else {
      // Merge filtered items uniquely into selections
      const filteredItemIds = filteredEmployees.map(e => e.id);
      setSelectedIds(prev => Array.from(new Set([...prev, ...filteredItemIds])));
    }
  };

  const handleConfirmSave = () => {
    onSave(selectedIds);
    setShow(false);
  };

  const filteredEmployees = allEmployees.filter((emp) =>
    (emp.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto">
        {/* Elite Backdrop Blur */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShow(false)}
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-[6px]"
        />

        {/* Modal Window Sheet */}
        <motion.div
          initial={{ scale: 0.98, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.98, opacity: 0, y: 10 }}
          transition={{ type: "spring", duration: 0.3, bounce: 0.05 }}
          className="bg-white rounded-xl w-full max-w-sm h-[550px] flex flex-col shadow-2xl border border-slate-200 relative z-10 my-auto overflow-hidden"
        >
          <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />

          {/* Header */}
          <div className="p-4 border-b border-slate-100 flex justify-between items-start shrink-0">
            <div>
                <h3 className="font-bold text-slate-900 text-sm tracking-tight flex items-center gap-2">
                  <Users size={15} className="text-[#002855]" /> Configure Active Roster
                </h3>
                <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                  Select which staff require attendance processing for this session.
                </p>
              </div>
            <button
              onClick={() => setShow(false)}
              className="p-1.5 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition"
            >
              <X size={16} />
            </button>
          </div>

          {/* Search bar & Select All Utility Controls */}
          <div className="p-3 bg-slate-50/70 border-b border-slate-100 space-y-2 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search staff members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white border border-slate-200 focus:border-[#002855] rounded-lg pl-8 pr-3 py-1.5 text-xs outline-none transition"
              />
            </div>
            
            <div className="flex items-center justify-between px-1 text-[10px]">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-[#002855] font-bold hover:underline"
              >
                {selectedIds.length === filteredEmployees.length ? "Deselect Filtered" : "Select All Filtered"}
              </button>
              <span className="text-slate-400 font-bold uppercase tracking-wider font-mono">
                {selectedIds.length} Selected
              </span>
            </div>
          </div>

          {/* Scrollable Staff Grid List Layout */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-slate-50/30">
            {filteredEmployees.length === 0 ? (
              <div className="text-center text-slate-400 text-xs py-12 font-medium">
                No matching staff profiles found.
              </div>
            ) : (
              filteredEmployees.map((emp) => {
                const isChecked = selectedIds.includes(emp.id);
                return (
                  <div
                    key={emp.id}
                    onClick={() => handleToggleEmployee(emp.id)}
                    className={`w-full flex items-center justify-between p-2.5 rounded-xl border text-left cursor-pointer transition ${
                      isChecked
                        ? "bg-blue-50/40 border-blue-200/60"
                        : "bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50/50"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-7 h-7 rounded-lg text-[11px] font-black flex items-center justify-center shrink-0 ${
                        isChecked ? "bg-[#002855] text-white" : "bg-slate-100 text-slate-600"
                      }`}>
                        {emp.name?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate">{emp.name}</p>
                        <p className="text-[9px] text-slate-400 font-mono">ID: {emp.position?.id_number || emp.id}</p>
                      </div>
                    </div>

                    <div className={`shrink-0 p-1 rounded-md transition ${
                      isChecked ? "text-[#002855]" : "text-slate-300"
                    }`}>
                      {isChecked ? <CheckSquare size={16} className="stroke-[2.5]" /> : <Square size={16} />}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Persistent Action Footer */}
          <div className="p-3 border-t border-slate-100 flex gap-2 justify-end bg-white shrink-0">
            <button
              type="button"
              onClick={() => setShow(false)}
              className="h-8 px-4 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-bold text-[11px] transition shadow-2xs"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmSave}
              className="h-8 px-4 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-white rounded-xl font-bold text-[11px] transition shadow-2xs flex items-center gap-1.5"
            >
              <Users size={12} />
              <span>Apply Selection</span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}